/**
 * `/admin?tab=settings` — now a fork, not a page.
 *
 * This used to be "Roles & access": role tiers, the user-roles directory and
 * property approvals. All three moved to Owners & access (/admin/owners) as
 * tabs, because keeping them here meant two screens each holding half the owner
 * picture — which is exactly how the owner list drifted out of sync.
 *
 * The address stays alive rather than 404ing, since it is bookmarked and linked
 * from older notes. Staff are forwarded to the merged page.
 *
 * OWNERS ARE NOT FORWARDED, and that distinction is the whole reason this
 * component still exists: signed in as an owner, this same URL is where you
 * change your own name, password and billing address. Redirecting everyone
 * would throw every owner onto a staff page they have no access to.
 */
import { Loader2 } from "lucide-react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { AdminOwnerSettings } from "@/components/admin/AdminOwnerSettings";
import { OperatorSettings } from "@/components/admin/OperatorSettings";

const AdminSettings = () => {
  const {
    loading: authLoading,
    isOwnerPortalOnly,
    isOperatorPortal,
  } = useAuth();

  // Wait for roles before deciding. Redirecting first would send owners to the
  // staff page for a frame, and bounce them straight back out.
  if (authLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2
          className="h-8 w-8 animate-spin text-muted-foreground"
          aria-label="Loading settings"
        />
      </div>
    );
  }

  if (isOwnerPortalOnly) {
    return <AdminOwnerSettings />;
  }

  // Operators manage only their own team — send them to their scoped settings,
  // not the staff Directory (COL-352 Phase 6).
  if (isOperatorPortal) {
    return <OperatorSettings />;
  }

  // Everyone else (staff): land on the DIRECTORY, not the owners list. Whoever
  // followed this link came for the roles table — dropping them somewhere
  // adjacent would still make them hunt for what they asked for. (It was a tab
  // on /admin/owners; it is its own page now, and this address redirects here.)
  return <Navigate to="/admin/directory" replace />;
};

export default AdminSettings;
